import { useState } from 'react';

import type { PreviewPage } from '../types';

interface UsePdfPagerProps {
  /**
   * Entries to page through. An entry with `pdfPage` shows that fixed page;
   * an entry without it spans its whole PDF — the pager walks through all of
   * its pages (count discovered once the document loads).
   */
  pages: PreviewPage[];
  /** Document url for an entry without its own `pdfUrl`. */
  fallbackUrl?: string;
  initialIndex?: number;
  /** Starting page within the initial whole-document entry (e.g. a citation). */
  initialPage?: number;
}

/** Which entry and page of a multi-document PDF preview is shown. */
export const usePdfPager = ({
  pages,
  fallbackUrl,
  initialIndex = 0,
  initialPage = 1
}: UsePdfPagerProps) => {
  const [index, setIndex] = useState(
    Math.min(Math.max(initialIndex, 0), Math.max(0, pages.length - 1))
  );
  // Page within the active whole-document entry (one without `pdfPage`).
  const [innerPage, setInnerPage] = useState(Math.max(1, initialPage));
  // Discovered page counts, per document url.
  const [pageCounts, setPageCounts] = useState<Record<string, number>>({});

  const activeEntry = pages[index];
  const isWholeDoc = !!activeEntry && activeEntry.pdfPage == null;
  const activeUrl = activeEntry?.pdfUrl ?? fallbackUrl;
  const docPages = activeUrl ? pageCounts[activeUrl] : undefined;

  const goPrev = () => {
    if (isWholeDoc && innerPage > 1) {
      setInnerPage(page => page - 1);
      return;
    }
    // Stepping back into a whole-document entry lands on its LAST page (when
    // its count is already known), mirroring forward paging.
    const prevIndex = Math.max(0, index - 1);
    const prevEntry = pages[prevIndex];
    const prevUrl = prevEntry?.pdfUrl ?? fallbackUrl;
    const prevIsWholeDoc = !!prevEntry && prevEntry.pdfPage == null;
    setIndex(prevIndex);
    setInnerPage(prevIsWholeDoc ? (prevUrl && pageCounts[prevUrl]) || 1 : 1);
  };

  const goNext = () => {
    if (isWholeDoc) {
      // The document's page count isn't known until it loads — stay put
      // rather than skipping its remaining pages.
      if (!docPages) return;
      if (innerPage < docPages) {
        setInnerPage(page => page + 1);
        return;
      }
    }
    setIndex(i => Math.min(pages.length - 1, i + 1));
    setInnerPage(1);
  };

  const registerPageCount = (numPages: number) => {
    if (!activeUrl) return;
    setPageCounts(prev =>
      prev[activeUrl] === numPages ? prev : { ...prev, [activeUrl]: numPages }
    );
  };

  return {
    activeUrl,
    activePage: isWholeDoc ? innerPage : (activeEntry?.pdfPage ?? 1),
    showPager: pages.length > 1 || (isWholeDoc && (docPages ?? 1) > 1),
    label: isWholeDoc
      ? `${activeEntry?.label} · page ${innerPage} of ${docPages ?? 1}`
      : `${activeEntry?.label} · page ${index + 1} of ${pages.length}`,
    prevDisabled: index === 0 && (!isWholeDoc || innerPage === 1),
    nextDisabled: isWholeDoc
      ? !docPages || (index === pages.length - 1 && innerPage >= docPages)
      : index === pages.length - 1,
    goPrev,
    goNext,
    registerPageCount
  };
};
